import styled from '@emotion/styled';
import { EnHeading } from 'components/atoms/enHeading';
import { TextButton } from 'components/atoms/textButton';
import { IndexLink } from 'components/molecules/indexLink';
import { ProjectCard } from 'components/molecules/projectCard';
import { StudioCard } from 'components/molecules/studioCard';
import { TopicsCard } from 'components/molecules/topicsCard';
import FadeUpSection from 'components/organisms/fadeUpSection';
import InquiryBlock from 'components/organisms/inquiryBlock';
import { LoopCarousel } from 'components/organisms/loopCarousel';
import ProjectCardCol from 'components/organisms/projectCardCol';
import Service from 'components/organisms/service';
import { TopicsGroup } from 'components/organisms/topicsGroup';
import { TopFact, TopFactOrganizer } from 'components/unique/topFacts';
import dayjs from 'dayjs';
import { graphql } from 'gatsby';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import colors from 'style/colors';
import fonts from 'style/fonts';
import { contentWrapper } from 'style/mixin';
import sizes from 'style/sizes';
import { baseTransitionSecond, breakpoint, mq } from 'style/variables';
import { IndexThQuery } from 'types/graphql-types';

import Fig01 from 'assets/images/top/fig-01.png';
import Fig02 from 'assets/images/top/fig-02.png';
import Fig03 from 'assets/images/top/fig-03.png';
import Fig04 from 'assets/images/top/fig-04.png';
import Fig05 from 'assets/images/top/fig-05.png';
import Fig06 from 'assets/images/top/fig-06.png';
import AnimationMemo from 'components/unique/animationMemo';
import AnimationMtg from 'components/unique/animationMtg';
import AnimationAward from 'components/unique/animationAward';
import AnimationMap from 'components/unique/animationMap';
import AnimationServices from 'components/unique/animationServices';
import AnimationWalk from 'components/unique/animationWalk';
import { css } from '@emotion/core';
import SEO from 'util/seo';

interface IndexPageProps {
  location: {
    pathname: string;
  };
  data: IndexThQuery;
}

const topmovie =
  'https://player.vimeo.com/video/968790245?h=69aaf994ae&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479';
const topmoviesp =
  'https://player.vimeo.com/video/969532758?h=08170d2539&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479';

const ContentsWrapper = styled.div`
  max-width: 1860px;
  margin-right: auto;
  margin-left: auto;
`;
const TopContent = styled.div`
  overflow: hidden;
  position: relative;
  margin-bottom: ${sizes.margin[100]};
  ${mq.onlysp} {
    padding: 0;
    margin-bottom: ${sizes.margin[120]};
  }
`;

const MainVisualWrapper = styled.div`
  background-color: ${colors.black01};
  position: relative;
  margin-bottom: 106px;
  ${mq.onlysp} {
    margin-bottom: 53px;
  }
`;

const Mainvisual = styled.div`
  position: relative;
  ${mq.onlypc} {
    width: 100%;
    aspect-ratio: 16 / 9;
    margin: ${sizes.header.height.pc} 0 0;
    overflow: hidden;
  }
  ${mq.onlysp} {
    width: 100%;
    aspect-ratio: 3 / 4;
    margin: ${sizes.header.height.sp} 0 0;
    overflow: hidden;
    &::before {
      content: '';
      display: block;
      padding-top: 100%;
    }
  }
`;

const MainvisualMovie = styled.div`
  ${mq.onlypc} {
    width: 100%;
    height: 100%;
    & > * {
      width: 100%;
      height: 100%;
    }
  }
  ${mq.onlysp} {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    & > * {
      width: 100%;
      height: 100%;
    }
  }
  & iframe {
    ${mq.onlypc} {
      min-width: 100vw;
      min-height: 100vh;
      width: auto;
      height: auto;
      position: relative;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
`;

const ScrollDown = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  ${mq.onlypc} {
    width: 60px;
    height: 180px;
    padding-bottom: 27px;
  }
  ${mq.onlysp} {
    width: 40px;
    height: 133px;
    padding-bottom: 15px;
  }
`;

const ScrollTxt = styled.div`
  writing-mode: vertical-rl;
  font-family: ${fonts.enGothic};
  color: ${colors.white01};
  text-align: center;
  margin: 0 auto;
  ${mq.onlypc} {
    font-size: 11px;
    letter-spacing: 0.22em;
    line-height: 1;
  }
  ${mq.onlysp} {
    font-size: 10px;
    letter-spacing: 0.2em;
    line-height: 1;
  }
`;

const LoadingText = styled.div<{ isLoading: boolean }>`
  width: 100%;
  height: 100%;
  background-color: ${colors.black01};
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: ${fonts.enGothic};
  color: ${colors.white01};
  transition: opacity ${baseTransitionSecond} ease;
  ${mq.onlypc} {
    font-size: 11px;
    letter-spacing: 0.22em;
  }
  ${mq.onlysp} {
    font-size: 10px;
    letter-spacing: 0.2em;
  }
  ${props =>
    props.isLoading
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `}
`;

const ScrollBar = styled.div`
  background-color: ${colors.gray02};
  margin: 0 auto;
  position: relative;
  ${mq.onlypc} {
    width: 1px;
    height: 80px;
    margin-top: 8px;
  }
  ${mq.onlysp} {
    width: 1px;
    height: 56px;
    margin-top: 8px;
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    background-color: ${colors.white01};
    animation: pathmove 1.6s ease-in-out infinite;
    animation-fill-mode: forwards;
    ${mq.onlypc} {
      height: 40px;
    }
    ${mq.onlysp} {
      height: 28px;
    }
  }
  @keyframes pathmove {
    0% {
      height: 0;
      top: 0;
    }
    50% {
      height: 100%;
      top: 0;
    }
    100% {
      height: 0;
      top: 100%;
    }
  }
`;

const SoundButton = styled.button`
  position: absolute;
  font-family: ${fonts.enGothic};
  color: ${colors.white01};
  ${mq.onlypc} {
    font-size: 11px;
    letter-spacing: 0.22em;
    right: 60px;
    bottom: 27px;
  }
  ${mq.onlysp} {
    font-size: 10px;
    letter-spacing: 0.2em;
    right: 40px;
    bottom: 15px;
  }
`;

const SectionTitleWrap = styled.div`
  text-align: center;
  ${mq.onlypc} {
    margin-bottom: ${sizes.margin[48]};
  }
  ${mq.onlysp} {
    margin-bottom: ${sizes.margin[32]};
  }
`;

const TopFactsWrap = styled.div`
  ${contentWrapper}
  ${mq.onlysp} {
    margin-top: ${sizes.margin[96]};
    padding: 0;
    margin-top: 0;
  }
`;

const TopFactsTitle = styled.div`
  margin-top: 4px;
  ${mq.onlysp} {
    padding: 0 ${sizes.margin[32]};
  }
`;

const TopFactsTitleBody = styled(EnHeading)`
  font-weight: 600 !important;
  letter-spacing: -1.05px;
  ${mq.onlysp} {
    font-size: 40px;
    line-height: 93%;
    letter-spacing: 0;
  }
`;

const TopFactsSubTitle = styled.div`
  font-size: 22px;
  font-weight: 500;
  font-family: ${fonts.enGothic};
  margin-bottom: 73px;
  margin-top: 10px;
  letter-spacing: 0;
  ${mq.onlysp} {
    margin-top: 16px;
    font-size: 20px;
    line-height: 128%;
    margin-bottom: 58px;
  }
`;

const ServiceWrap = styled.div`
  ${contentWrapper}
  margin-top: ${sizes.margin[100]};
  ${mq.onlysp} {
    padding: 0;
    margin-top: ${sizes.margin[80]};
  }
`;

const ProjectsWrap = styled.div`
  ${contentWrapper}
  margin-top: ${sizes.margin[100]};
  ${mq.onlysp} {
    padding: 0;
    margin-top: ${sizes.margin[80]};
  }
`;

const TopicsWrap = styled.div`
  ${mq.onlypc} {
    ${contentWrapper}
    padding: 0;
    margin-top: ${sizes.margin[100]};
  }
  ${mq.onlysp} {
    margin-top: ${sizes.margin[80]};
  }
`;

const StudioWrap = styled.div`
  ${mq.onlypc} {
    margin-top: ${sizes.margin[100]};
  }
  ${mq.onlysp} {
    margin-top: ${sizes.margin[80]};
  }
`;

const ProjectTitle = styled('div')`
  width: 100%;
  margin: 0 auto;
  ${mq.onlysp} {
    padding: 0 ${sizes.margin[32]};
  }
`;

const TopicsTitle = styled('div')`
  ${contentWrapper}

  width: 100%;
  margin: 0 auto;
  ${mq.onlypc} {
    padding: 0;
  }
  ${mq.onlysp} {
    padding: 0;
  }
`;

const StudiosTitle = styled('div')`
  ${contentWrapper}

  width: 100%;
  margin: 0 auto;
  ${mq.onlypc} {
    padding: 0;
  }
  ${mq.onlysp} {
    padding: 0;
  }
`;

const ViewMoreLinkWrap = styled(IndexLink)`
  display: block;
  margin-top: ${sizes.margin[48]};
  text-align: center;

  ${({ to }) =>
    to === '/topics' &&
    css`
      margin-top: 26px;
    `}

  ${mq.onlysp} {
    margin-top: ${sizes.margin[16]};
    padding-bottom: 0;
  }
`;

const FactsAnd = styled.span`
  font-size: 28px;
  font-weight: 700;
  display: inline-block;
  margin: 0 6px;
  ${mq.onlysp} {
    margin: 0 -4px;
  }
`;

const Pages: React.FC<IndexPageProps> = ({ location, data }) => {
  const topics = data.allTopicsEnJson.edges;
  const studios = data.allShifterStudiosData.edges;
  const projectsOldLayout = data.projects.edges;
  const projectsNewLayout = data.projectsJp.edges;
  const projects = projectsNewLayout.concat(projectsOldLayout);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pickups = (data.shifterPickupProjects as any)?.edges?.[0]?.node?.acf?.pickup;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const pickupProjects = pickups!
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((pickup: any) => projects?.filter((project: any) => project?.node?.title?.rendered === pickup?.post_title))
    .flat();

  const [isSp, setIsSp] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isBrowser, setIsBrowser] = useState(false);
  const [ReactPlayer, setReactPlayer] = useState<any>(null);

  const resizeHandler = useCallback(() => {
    setIsSp(window.matchMedia(`(max-width:${breakpoint}px)`).matches);
  }, []);

  const scrollHandler = useCallback(() => {
    const tmpIsScrollDown = window.scrollY > 100;
    if (tmpIsScrollDown) {
      window.removeEventListener('scroll', scrollHandler);
    }
  }, []);

  useEffect(() => {
    setIsBrowser(true);
    // Load ReactPlayer dynamically only on the client side
    import('react-player').then(module => {
      setReactPlayer(() => module.default);
    });
  }, []);

  useEffect(() => {
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [resizeHandler]);

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [scrollHandler]);

  const mainvisualRef = useRef<HTMLDivElement>(null);

  const onStart = () => {
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  return (
    <>
      <SEO locationPathName={location?.pathname} />

      <MainVisualWrapper>
        <Mainvisual ref={mainvisualRef}>
          <MainvisualMovie>
            {isBrowser && ReactPlayer && (
              <ReactPlayer
                url={isSp ? topmoviesp : topmovie}
                playing
                playsinline
                muted={isMuted}
                loop
                width="100%"
                height="100%"
                onReady={onStart}
              />
            )}
          </MainvisualMovie>
          <LoadingText isLoading={isLoading}>Loading...</LoadingText>
        </Mainvisual>
        <ScrollDown>
          <ScrollTxt>Scroll</ScrollTxt>
          <ScrollBar />
        </ScrollDown>
        <SoundButton onClick={() => setIsMuted(!isMuted)}>{isMuted ? 'Mute' : 'Unmute'}</SoundButton>
      </MainVisualWrapper>
      <TopContent>
        <ContentsWrapper>
          <TopFactsWrap>
            <TopFactsTitle>
              <TopFactsTitleBody size="large">
                Facts <FactsAnd>&</FactsAnd>
                <br className="only-sp" /> Figures
              </TopFactsTitleBody>
              <TopFactsSubTitle>
                Showcasing <br className="only-sp" /> Who We Are
              </TopFactsSubTitle>
            </TopFactsTitle>
            <TopFactOrganizer>
              <TopFact
                mainImage={<AnimationMemo />}
                title="จำนวนรวมของผู้ตอบแบบสอบถาม"
                counts={400}
                unit={
                  <>
                    <span className="unit-alphabet">K</span>
                    <span className="unit-plus">+</span>
                    {'　'}
                  </>
                }
                subImage1={Fig02}
                subImage2={Fig02}
                subImage1Count={4}
                subImage2Count={15}
                type={'0'}
              />
              <TopFact
                mainImage={<AnimationMtg />}
                title="จำนวนรวมของการสัมภาษณ์ผู้ใช้งาน"
                counts={2200}
                unit={
                  <>
                    <span className="unit-plus">+</span>
                    {'　'}
                  </>
                }
                subImage1={Fig01}
                subImage2={Fig01}
                subImage1Count={2}
                subImage2Count={2}
                type={'1'}
              />
              <TopFact
                mainImage={<AnimationMap />}
                title={'การวิจัยภาคสนาม'}
                counts={66}
                unit={<span>เมือง</span>}
                subImage1={Fig04}
                subImage2={Fig04}
                subImage1Count={5}
                subImage2Count={6}
                type={'2'}
              />
              <TopFact
                mainImage={<AnimationWalk />}
                title={
                  <>
                    ระยะทางรวมของการเดินทาง
                    <br />
                    ลงพื้นที่เพื่อทำวิจัยทั้งหมด
                  </>
                }
                counts={40}
                unitPre=""
                unit={
                  <span>
                    รอบโลก
                    <br />
                    <span className="unit-per">รอบ</span>
                  </span>
                }
                subImage1={Fig03}
                subImage2={Fig03}
                subImage1Count={4}
                subImage2Count={0}
                type={'3'}
              />
              <TopFact
                mainImage={<AnimationAward />}
                title="Awards"
                counts={80}
                unit={
                  <>
                    <span className="unit-plus">+</span>
                  </>
                }
                subImage1={Fig05}
                subImage2={Fig05}
                subImage1Count={8}
                subImage2Count={5}
                type={'4'}
              />
              <TopFact
                mainImage={<AnimationServices />}
                title={<>จำนวนรวมของผู้ใช้บริการ</>}
                counts={160}
                unit={
                  <>
                    <span className="unit-alphabet">M</span>
                    <span className="unit-plus">+</span>
                    {'　'}
                  </>
                }
                subImage1={Fig06}
                subImage2={Fig06}
                subImage1Count={17}
                subImage2Count={0}
                type={'5'}
              />
            </TopFactOrganizer>
          </TopFactsWrap>
          <ServiceWrap>
            <Service top={true} pageLang="th" />
          </ServiceWrap>
          <ProjectsWrap>
            <SectionTitleWrap>
              <ProjectTitle>
                <EnHeading>Projects</EnHeading>
              </ProjectTitle>
            </SectionTitleWrap>
            <ProjectCardCol col={2}>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {pickupProjects?.map((project: any) => (
                <FadeUpSection key={`project-${project.node.id}`}>
                  <ProjectCard
                    size={'large'}
                    spSize="small"
                    imgSpSize="full"
                    imgpath={
                      project?.node?.acf?.thumb ||
                      project?.node?.acf?.kv ||
                      project?.node?.acf?.kv_sp ||
                      'noimage/img-card-thumb.jpg'
                    }
                    title={project.node.acf?.page_headline_ja || project.node.title?.rendered || 'No Title'}
                    client={project.node.acf?.client_abbr?.en || undefined}
                    work={project?.node?.acf?.product?.en || undefined}
                    tags={project.node.acf?.outline as string[]}
                    to={`/th/projects/${project.node.slug}`}
                  />
                </FadeUpSection>
              ))}
            </ProjectCardCol>
            <ViewMoreLinkWrap to={'/th/projects'}>
              <TextButton isBlock>Our Projects</TextButton>
            </ViewMoreLinkWrap>
          </ProjectsWrap>
          <TopicsWrap>
            <SectionTitleWrap>
              <TopicsTitle>
                <EnHeading>Topics</EnHeading>
              </TopicsTitle>
            </SectionTitleWrap>
            <FadeUpSection>
              <TopicsGroup spslide>
                {topics?.map(topic => {
                  return (
                    <TopicsCard
                      key={topic.node.id}
                      title={topic?.node.title?.rendered || 'NO TITLE'}
                      date={dayjs(topic?.node.acf?.launch || undefined).format('YYYY.MM.DD')}
                      to={`/th/topics/${topic?.node.slug}`}
                      imgpath={topic?.node.acf?.thumb || 'noimage/img-card-thumb.jpg'}
                      category={topic?.node.acf?.category || ''}
                    />
                  );
                })}
              </TopicsGroup>
              <ViewMoreLinkWrap to={'/th/topics'}>
                <TextButton isBlock>View More</TextButton>
              </ViewMoreLinkWrap>
            </FadeUpSection>
          </TopicsWrap>
          <StudioWrap>
            <SectionTitleWrap>
              <StudiosTitle>
                <EnHeading>Studios</EnHeading>
              </StudiosTitle>
            </SectionTitleWrap>
            <FadeUpSection>
              <LoopCarousel slidesToShow={2.4} padding={82}>
                {studios.map(studio => (
                  <StudioCard
                    key={studio.node.id}
                    imgpath={studio.node.acf?.thumb || 'noimage/img-card-thumb.jpg'}
                    {...(studio.node.acf?.detail_page ? { to: `/th/about/studios/${studio.node.slug}` } : {})}
                    place={studio.node.title?.rendered || ''}
                  />
                ))}
              </LoopCarousel>
            </FadeUpSection>
          </StudioWrap>
        </ContentsWrapper>
      </TopContent>
      <InquiryBlock top isStaticSp={false} th />
    </>
  );
};

export default Pages;

export const pageQuery = graphql`
  query IndexTh {
    site {
      siteMetadata {
        title
      }
    }
    shifterPickupProjects: allPickupProjectsJson(filter: { title: { rendered: { eq: "allpickupprojects" } } }) {
      edges {
        node {
          acf {
            pickup {
              post_title
            }
          }
        }
      }
    }
    projectsJp: allProjectsJpJson(
      sort: { fields: date, order: DESC }
      filter: { title: { rendered: { nin: ["dummy", null] } } }
    ) {
      edges {
        node {
          id
          title {
            rendered
          }
          slug
          acf {
            page_headline_ja
            card_headline_ja
            page_headline_en
            card_headline_en
            headline_en
            pickup
            client_abbr {
              ja
              en
            }
            services {
              post_title
              post_name
            }
            kv
            kv_sp
            outline
            product {
              ja
              en
            }
            thumb
            launch
          }
        }
      }
    }
    projects: allProjectsJson(
      sort: { fields: date, order: DESC }
      filter: { title: { rendered: { nin: ["dummy", null] } } }
    ) {
      edges {
        node {
          id
          title {
            rendered
          }
          slug
          acf {
            page_headline_ja
            card_headline_ja
            headline_en
            client_abbr {
              ja
              en
            }
            services {
              post_title
              post_name
            }
            kv
            kv_sp
            outline
            pickup
            product {
              ja
              en
            }
            thumb
            launch
          }
        }
      }
    }
    allTopicsEnJson(
      sort: { fields: acf___launch, order: DESC }
      filter: { title: { rendered: { nin: ["dummy", null] } } }
      limit: 6
    ) {
      edges {
        node {
          id
          title {
            rendered
          }
          acf {
            category
            thumb
            launch
          }
          slug
        }
      }
    }
    allShifterStudiosData: allStudiosJson(
      filter: { title: { rendered: { nin: ["dummy", null] } } }
      sort: { order: ASC, fields: acf___show_num }
    ) {
      edges {
        node {
          id
          title {
            rendered
          }
          slug
          acf {
            thumb
            detail_page
          }
        }
      }
    }
  }
`;
